import { z } from 'zod';
import { ChartData, ScriptableContext } from 'chart.js';
import { GenericChart, GenericChartProps } from 'app/agriculture/charts/GenericChart/GenericChart';
import { Bar } from 'react-chartjs-2';
import { startOfHour } from 'date-fns';
import { useTimelineCursorTime } from 'app/agriculture/AgTimeline/context/TimelineContext';
import { getDefaultBarChartOptions } from 'app/agriculture/charts/BarChart/DefaultBarChartOptions';
import { ChartContext } from 'app/agriculture/charts/ChartContextProvider';
import { useContext } from 'react';
import { TZDate } from '@date-fns/tz';
import { useTimeZone } from 'app/agriculture/hooks/useTimeZone/useTimeZone';
import { primaryColourMain } from 'app/agriculture/material';

function backgroundColor(context: ScriptableContext<'bar'>): string {
    const value = z.union([z.number(), z.nan()]).parse(context.dataset.data[context.dataIndex]);
    return value === 0 ? 'rgba(113, 113, 113, 0.1)' : primaryColourMain;
}

export function BarChart({
    data,
    title,
    displayUnit: unit,
    showCurrentValue = true,
}: GenericChartProps) {
    const { timeZone } = useTimeZone();
    const { intervalUnit, dateFormat } = useContext(ChartContext);
    const cursorTime = useTimelineCursorTime() ?? new TZDate(new Date(), timeZone);
    const currentHour = startOfHour(cursorTime);
    // The regex removes milliseconds. See: https://stackoverflow.com/a/65076090
    const currentValue = data[currentHour.toISOString().replace(/[.]\d+/, '')];

    const chartData: ChartData<'bar'> = {
        labels: Object.keys(data),
        datasets: [
            {
                data: Object.values(data),
                backgroundColor: backgroundColor,
                borderSkipped: false,
                borderRadius: 15,
                minBarLength: 15,
                maxBarThickness: 24,
            },
        ],
    };

    const chartOptions = getDefaultBarChartOptions({
        intervalUnit: intervalUnit,
        dateFormat,
        timeZone,
    });

    return (
        <GenericChart title={title}>
            {showCurrentValue ? (
                <h5>
                    {currentValue == null || isNaN(currentValue)
                        ? 'Unknown'
                        : `${currentValue}${unit}`}
                </h5>
            ) : null}
            <Bar data={chartData} options={chartOptions} />
        </GenericChart>
    );
}
